<template>
    <div class="mb-5">
        <h5>{{ $t('Director Information') }}</h5>

        <div class="mb-3 mt-4">
            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Director Name') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.directorName" @keydown="onChange('directorName')"/>
                    <div v-if="error['directorName']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorName'])}}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Director Birth Date') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <date-picker v-model="params.directorBirthDate" :lang="lang_date" input-class="form-control" format="YYYY-MM-DD" value-type="YYYY-MM-DD" @change="onChange('directorBirthDate')"></date-picker>
                    <div v-if="error['directorBirthDate']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorBirthDate'])}}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Director ID Number') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" placeholder="ID Number" v-model="params.directorIdNo" @keydown="onChange('directorIdNo')"/>
                    <div v-if="error['directorIdNo']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorIdNo'])}}
                    </div>
                </b-col>
            </b-row>
            
            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t("Type of director's ID") }} <span class="text-danger">*</span></label>
                </b-col>
                <b-col sm="12" md="9">
                    <NewDocOption 
                        :data="doc_type_list"
                        :ref="'doc_type'"
                        @selected-document="selectedDocument($event)">
                    </NewDocOption>
                    <div v-if="error['directorIdType']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorIdType'])}}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('ID Photo') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="3">
                    <div class="d-flex justify-content-center">
                        <label class="pt-2">{{ $t('ID Photo Front Side') }} <span class="text-danger">*</span></label>
                        <span class="position-relative">
                            <button type="button" class="flat-button position-absolute" @click="togglePopup(1)">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                            </button>
                            <NewPopupInfo :name="(doc_type==0?'ic':'passport')+'_front'" v-if="showPopup_1" :addClass="'popup-info-center'"></NewPopupInfo>
                        </span>
                    </div>

                    <FileUploader  
                        :type="'browse'"
                        :ref="'directorIdFront'"
                        :allowed_types = "['jpg','jpeg', 'png']"
                        :size="4*1024*1024"
                        @capture-file="captureFile($event,'directorIdFront')" 
                        @remove-image="removeImage($event)"
                        @toggle-camera="toggleCamera($event)">
                    </FileUploader>
                    <div v-if="error['directorIdFront']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorIdFront'])}}
                    </div>
                </b-col>
                <b-col sm="12" md="3" v-if="params.directorIdType == 3 || params.directorIdType == 4">
                    <div class="d-flex justify-content-center">
                        <label class="pt-2">{{ $t('ID Photo Back Side') }} <span class="text-danger">*</span></label>
                        <span class="position-relative">
                            <button type="button" class="flat-button position-absolute" @click="togglePopup(2)">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                            </button>
                            <NewPopupInfo :name="(doc_type==0?'ic':'passport')+'_national'" v-if="showPopup_2" :addClass="'popup-info-center'"></NewPopupInfo>
                        </span>
                    </div>
                    <FileUploader  
                        :type="'browse'"
                        :ref="'directorIdReverse'"
                        :allowed_types = "['jpg','jpeg', 'png']"
                        :size="4*1024*1024"
                        @capture-file="captureFile($event,'directorIdReverse')" 
                        @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['directorIdReverse']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorIdReverse'])}}
                    </div>
                </b-col>
                <b-col sm="12" md="3">
                    <div class="d-flex justify-content-center">
                        <label class="pt-2">{{ $t('Holding ID Card') }} <span class="text-danger">*</span></label>
                        <span class="position-relative">
                            <button type="button" class="flat-button position-absolute" @click="togglePopup(3)">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                            </button>
                            <NewPopupInfo :name="(doc_type==0?'ic':'passport')+'_holding'" v-if="showPopup_3" :addClass="'popup-info-center'" :styleCustom="{right: 'calc(15vw)'}"></NewPopupInfo>
                        </span>
                    </div>

                    <FileUploader  
                        :type="'browse'"
                        :ref="'directorIdHold'"
                        :allowed_types = "['jpg','jpeg', 'png']"
                        :size="4*1024*1024"
                        @capture-file="captureFile($event,'directorIdHold')" 
                        @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['directorIdHold']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorIdHold'])}}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t("Validity period of directors' certificates") }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <date-picker v-model="params.directorCertExpiryDate" :lang="lang_date" input-class="form-control" format="YYYY-MM-DD" value-type="YYYY-MM-DD" @change="onChange('directorCertExpiryDate')"></date-picker>
                    <div v-if="error['directorCertExpiryDate']" class="invalid-feedback d-block mb-3">
                        {{$t(error['directorCertExpiryDate'])}}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('Province') }} <span class="text-danger">*</span></label>
                        <Select2 v-model="params.directorProvince" :options="provinceOpt" @select="onSelectProvince($event)" :settings='{ escapeMarkup: doEscapeMarkup,placeholder:$t("Province")}' class="form-control"/>
                        <div v-if="error['directorProvince']" class="invalid-feedback d-block mb-3">
                            {{$t(error['directorProvince'])}}
                        </div>
                    </div>
                </b-col>
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('City') }} <span class="text-danger">*</span></label>
                        <Select2 v-model="params.directorCity" :options="cityOpt" @select="onSelectCity($event)" :settings='{ escapeMarkup: doEscapeMarkup,placeholder:$t("City")}' class="form-control"/>
                        <div v-if="error['directorCity']" class="invalid-feedback d-block mb-3">
                            {{$t(error['directorCity'])}}
                        </div>
                    </div>
                </b-col>
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('District') }}</label>
                        <Select2 v-model="params.directorArea" :options="districtOpt" @select="onSelectDistrict($event)" :settings='{ escapeMarkup: doEscapeMarkup,placeholder:$t("District")}' class="form-control"/>
                        <div v-if="error['directorArea']" class="invalid-feedback d-block mb-3">
                            {{$t(error['directorArea'])}}
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col xl="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('Address') }} <span class="text-danger">*</span></label>
                        <input type="text" name="directorAddress" v-model="params.directorAddress" class="form-control" @keydown="addressCondition($event)" @keyup="onchangeAddress($event)"/>
                        <div v-if="error['directorAddress']" class="invalid-feedback d-block mb-3">
                            {{$t(error['directorAddress'])}}
                        </div>
                    </div>
                </b-col>
            </b-row>
        </div>

    </div>


</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Select2 from 'v-select2-component';

import NewDocOption from "@/components/Kyc/NewDocOption";
import FileUploader from '@/components/Kyc/FileUploader.vue';
import NewPopupInfo from '@/components/NewPopupInfo.vue';
import CreateAccount from '@/services/CreateAccount';

import 'vue2-datepicker/locale/zh-cn';
import 'vue2-datepicker/locale/zh-tw';
import 'vue2-datepicker/locale/ja';
import 'vue2-datepicker/locale/en';

export default {
  data() {
    return {
        publicPath :process.env.BASE_URL!='/'?process.env.BASE_URL:'',
        doc_type:0,
        showPopup_1:false,
        showPopup_2:false,
        showPopup_3:false,
        maxSize:4,
        success:null,
        userNo:null,
        accountId:null,
        lang_date:'en',
        cityOpt:[],
        districtOpt:[],
        provinceOpt:[],
        optional:['directorArea'],
        file:{
            directorIdFront: null,
            directorIdReverse: null,
            directorIdHold: null
        },
        params:{
            directorName: '',
            directorBirthDate: '',
            directorIdNo: '',
            directorIdType: '',     
            directorIdFront: '',
            directorIdReverse: '',
            directorIdHold: '',
            directorCertExpiryDate: '',
            directorProvince: '',
            directorCity: '',                
            directorArea:'',
            directorAddress:''
        },
        error:{
            directorName: '',
            directorBirthDate: '',
            directorIdNo: '',
            directorIdType: '',     
            directorIdFront: '',
            directorIdReverse: '',
            directorIdHold: '',
            directorCertExpiryDate: '',
            directorProvince: '',
            directorCity: '',                
            directorArea:'',
            directorAddress:''
        }
    }
  },
  components:{
    NewDocOption,
    FileUploader,
    Select2,
    NewPopupInfo,
    DatePicker
  },
  props:['doc_type_list','information','user_no','account_id'],
  methods:{
    getFile(index,file_id){
        let self = this;
        CreateAccount.getFile({fileId:file_id}).then(response => {
            let data_file = response.data.data[0];
            if(data_file.type == 2 || data_file.type == 5){
                self.params.directorIdFront = data_file.file;
                self.$refs['directorIdFront'].image =data_file.file;
            }
            else if(data_file.type == 4 || data_file.type == 6){
                self.params.directorIdHold = data_file.file;
                self.$refs['directorIdHold'].image =data_file.file;
            }
            else if(data_file.type == 3){
                self.params.directorIdReverse = data_file.file;
                self.$refs['directorIdReverse'].image =data_file.file;
            }
        });
    },
    addressCondition($event){
        if($event.keyCode == 13){
            $event.preventDefault();
        }
        this.onChange('directorAddress');
    },
    onchangeAddress($event){
        let msg = $event.target.value.replace("\n", " ");
        $event.target.value = msg;
    },
    onSelectDistrict({id,text}){
        var self = this;
        this.onChange('directorArea');
    },
    onSelectCity({id,text,code}){
        var self = this;
        this.onChange('directorCity');
        if(text){
            this.directorArea="";
        }
        this.getDistrict(code);
    },
    onSelectProvince({id,text,code}){
        var self = this;
        this.onChange('directorProvince');
        self.districtOpt = [];
        if(text){
            this.directorCity = "";
            this.directorArea = "";
        }
        this.getCity(code);
    },
    getDistrict(code){
        this.districtOpt = [];
        CreateAccount.getDistrict({"city_id":code}).then(response => {
            const data = response.data.data;
            data.forEach(item => {
                this.districtOpt.push({"id":item.value,"text":item.text,"code":item.code});
            });
        }).catch(err => {
            this.checkError(err);
        });
    },
    getCity(code){
        this.cityOpt = [];
        CreateAccount.getCity({"province_id":code}).then(response => {
            const data = response.data.data;
            data.forEach(item => {
                this.cityOpt.push({"id":item.value,"text":item.text,"code":item.code});
            });
        }).catch(err => {
            this.checkError(err);
        });
    },
    onChange(name){
        this.error[name]="";
    },
    validate(){
        let success = true;
        let self = this;
        Object.keys(this.params).forEach(function(key) {
            if(self.params.directorIdType==2 && key == "directorIdReverse"){
                self.error[key]="";
            }
            else if(!self.params[key] && !self.optional.includes(key)){
                self.error[key]="can not be blank.";
                console.log(key+" can not be blank.");
                success = false;
            }
        });

        return success;
    },
    uploadFile(account_id,user_no){
        let self = this;
        this.success = null;
        this.accountId = account_id;
        this.userNo = user_no;

        this.error.directorIdFront='';
        this.error.directorIdHold='';
        this.error.directorIdReverse='';

        let fileList = [
            {
                "type":(self.params.directorIdType==3 || self.params.directorIdType==4)?2:5,
                "maxSize":self.maxSize*1024*1024,
                "file":self.params.directorIdFront
            },
            {
                "type":(self.params.directorIdType==3 || self.params.directorIdType==4)?4:6,
                "maxSize":self.maxSize*1024*1024,
                "file":self.params.directorIdHold
            }
        ];

        if(self.params.directorIdType==3 || self.params.directorIdType==4){
            fileList.push(
                {
                    "type":3,
                    "maxSize":self.maxSize*1024*1024,
                    "file":self.params.directorIdReverse
                }
            );
        }

        CreateAccount.uploadFile({
            "accountId":self.accountId,
            "userNo":""+self.userNo,
            "fileUpload":fileList
        }).then(response => {
            const data = response.data.data;
            let fileList = data["file"];
            for(let i=0;i<fileList.length;i++){
                if(fileList[i].type==2 || fileList[i].type==5){
                    // self.params.directorIdFront = fileList[i].file;
                    self.file.directorIdFront = fileList[i].fileId;
                }
                else if(fileList[i].type==4 || fileList[i].type==6){
                    // self.params.directorIdHold = fileList[i].file;
                    self.file.directorIdHold = fileList[i].fileId;
                }
                else if(fileList[i].type==3){
                    // self.params.directorIdReverse = fileList[i].file;
                    self.file.directorIdReverse = fileList[i].fileId;
                }
            }
            self.success = true;
            self.$emit('file-uploaded');
        }).catch(err => {
            self.checkError(err);
        });
    },
    checkError(err){
        try{
            let self = this;
            const response = err.response;  
            if(response.status == 400){
                if(typeof response.data.message === 'string'){
                    this.$notify({
                        type: 'warn',
                        text: response.data.message
                    });
                }
                else{
                    if(response.data.message['file']){
                        let fileList = response.data.message['file'];
                        for(let i=0;i<fileList.length;i++){
                            if(fileList[i].type==2 || fileList[i].type==5){
                                self.error.directorIdFront=fileList[i].message;
                            }
                            else if(fileList[i].type==4 || fileList[i].type==6){
                                self.error.directorIdHold=fileList[i].message;
                            }
                            else if(fileList[i].type==3){
                                self.error.directorIdReverse=fileList[i].message;
                            }
                        }
                    }
                    self.success = false;
                    self.$emit('file-uploaded');
                }
            }      
            else if(response.status == 401 && response.data.name == "Unauthorized"){
                    localStorage.removeItem("token");
                    localStorage.removeItem("member");
                    localStorage.removeItem("isFirstTime");
                    this.$router.push({ 
                        path: '/login', 
                    });
            }
            else{
                if(localStorage.getItem("token")){
                    var msg = (response.data)?response.data.message:err.message;
                    this.$notify({
                        type: 'warn',
                        text: msg
                    })
                }
            }
        }catch(e){
            console.log(err);
        }
    },
    togglePopup(index){
        this['showPopup_'+index]=!this['showPopup_'+index];
    },
    selectedDocument(data){
        this.params.directorIdType = data;
        this.error.directorIdType = "";
    },
    captureFile($event,key) {
        this.params[key]=$event;
        this.error[key]="";
    },
    removeImage($event){
        console.log($event);
    },
    doEscapeMarkup(markup){
        return markup;
    }
  },
  mounted(){
    let self = this;
    this.$nextTick(() => {
    
        CreateAccount.getProvince({"country":"HKG"}).then(response => {
            const data = response.data.data;
            this.provinceOpt = [];
            data.forEach(item => {
                this.provinceOpt.push({"id":item.value,"text":item.text,"code":item.id});    
            });
            
            if(this.information){
                 
                Object.keys(this.params).forEach(function(key) {
                    if(typeof self.file[key]!=="undefined" && self.information[key]){
                        self.file[key]=self.information[key];
                        self.getFile(key,self.information[key]);
                    }
                    else{
                        self.params[key]=self.information[key];
                    }
                });

                if(self.information['directorIdType']){
                    self.$refs['doc_type'].doc_type = parseInt(self.information['directorIdType']);
                }
                else{
                    self.$refs['doc_type'].doc_type = '';
                }
                
                if(this.params.directorProvince){
                    let data_province = this.params.directorProvince.split("_");
                    this.getCity(data_province[0]);
                }
                
                if(this.params.directorCity){
                    let data_city = this.params.directorCity.split("_");
                    this.getDistrict(data_city[0]);
                }
            }

            if(this.user_no){
                this.userNo = this.user_no;
                this.accountId = this.account_id;
            }

        }).catch(err => {
            this.checkError(err);
        });
    });
  }
}
</script>